const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const money = (value, currency = 'aud') => {
  const amount = Number(value) || 0;
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: String(currency || 'aud').toUpperCase(),
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const fmtDate = (value, withTime = false) => {
  if (!value) return '—';
  const options = withTime
    ? { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { day: 'numeric', month: 'short', year: 'numeric' };
  return new Date(value).toLocaleString('en-AU', options);
};

const human = (value) => String(value || '—').replaceAll('_', ' ');

export const invoicePrintHtml = ({ invoice, kind = 'client', booking = {} }) => {
  const isClient = kind === 'client';
  const currency = invoice?.currency || 'aud';
  const logoUrl = `${window.location.origin}/images/logo.webp`;
  const title = isClient ? 'Client Invoice / Receipt' : 'Gigger Payout Invoice';
  const partyName = invoice?.party?.name || (isClient ? booking.client?.name : booking.gigger?.name) || '—';
  const partyEmail = invoice?.party?.email || (isClient ? booking.client?.email : booking.gigger?.email) || '';
  const trackingId = booking.trackingId || String(invoice?.invoiceNumber || '').replace(/^INV-/, '').replace(/-[CG]$/, '') || '—';
  const serviceName = booking.service?.name || booking.serviceName || 'Service';
  const scheduled = booking.scheduledDate ? fmtDate(booking.scheduledDate) : '';
  const time = booking.startTime && booking.endTime ? `${booking.startTime}–${booking.endTime}` : '';

  const lineItems = (invoice?.lineItems || []).map((item) => `
    <tr>
      <td>${escapeHtml(item.description)}</td>
      <td class="right">${item.hours != null ? escapeHtml(item.hours) : '—'}</td>
      <td class="right">${escapeHtml(money(item.unitAmount, currency))}</td>
      <td class="right strong">${escapeHtml(money(item.amount, currency))}</td>
    </tr>`).join('');

  const clientTotals = isClient ? `
    <div class="total-row"><span>Amount paid</span><strong>${escapeHtml(money(invoice?.amountPaid, currency))}</strong></div>
    ${(Number(invoice?.amountRefunded) || 0) > 0 ? `<div class="total-row"><span>Refunded</span><strong>${escapeHtml(money(invoice.amountRefunded, currency))}</strong></div>` : ''}
  ` : '';

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${escapeHtml(invoice?.invoiceNumber || 'GIGGS Invoice')}</title>
  <style>
    *{box-sizing:border-box}
    body{margin:0;background:#edf2ef;color:#183126;font-family:Arial,Helvetica,sans-serif}
    .toolbar{position:sticky;top:0;z-index:2;display:flex;justify-content:flex-end;padding:12px 20px;background:#173a2b}
    .toolbar button{appearance:none;border:0;border-radius:8px;padding:10px 16px;background:#209668;color:#fff;font-weight:700;cursor:pointer}
    .sheet{width:210mm;min-height:297mm;margin:22px auto;background:#fff;padding:20mm 18mm;box-shadow:0 10px 32px rgba(0,0,0,.12)}
    .top{display:flex;justify-content:space-between;gap:30px;border-bottom:2px solid #209668;padding-bottom:20px}
    .brand-logo{display:block;width:125px;max-width:100%;height:auto}
    .muted{color:#71877c;font-size:12px;line-height:1.6}
    h1{font-size:22px;margin:0 0 6px}
    .invoice-meta{text-align:right}
    .invoice-meta strong{display:block;font-size:15px;margin-bottom:4px}
    .parties{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin:28px 0}
    .box{border:1px solid #dfe8e3;border-radius:10px;padding:14px}
    .box label{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#81948a;margin-bottom:7px}
    .box strong{font-size:14px}
    table{width:100%;border-collapse:collapse;margin-top:8px}
    th{background:#f4f8f6;color:#64786d;text-align:left;text-transform:uppercase;font-size:10px;letter-spacing:.06em;padding:11px 10px;border-bottom:1px solid #dfe8e3}
    td{padding:12px 10px;border-bottom:1px solid #edf2ef;font-size:12px}
    .right{text-align:right}.strong{font-weight:800}
    .totals{width:330px;max-width:100%;margin:22px 0 0 auto}
    .total-row{display:flex;justify-content:space-between;gap:20px;padding:7px 0;font-size:12px}
    .total-row.final{border-top:2px solid #173a2b;margin-top:7px;padding-top:12px;font-size:17px;font-weight:900}
    .footer{margin-top:42px;padding-top:18px;border-top:1px solid #e5ece8;color:#7d8e85;font-size:10px;line-height:1.6}
    @media print{body{background:#fff}.toolbar{display:none}.sheet{box-shadow:none;margin:0;width:auto;min-height:auto;padding:12mm}}
    @media(max-width:850px){.sheet{width:100%;margin:0;min-height:100vh;padding:24px}.parties{grid-template-columns:1fr}.invoice-meta{text-align:left}.top{flex-direction:column}}
  </style>
</head>
<body>
  <div class="toolbar"><button onclick="window.print()">Print / Save PDF</button></div>
  <main class="sheet">
    <div class="top">
      <div><img class="brand-logo" src="${escapeHtml(logoUrl)}" alt="GIGGS" /><div class="muted" style="margin-top:8px">Marketplace payment document</div></div>
      <div class="invoice-meta"><h1>${escapeHtml(title)}</h1><strong>${escapeHtml(invoice?.invoiceNumber || 'Pending')}</strong><div class="muted">Issued ${escapeHtml(fmtDate(invoice?.issuedAt, true))}<br />Status: ${escapeHtml(human(invoice?.status))}</div></div>
    </div>
    <div class="parties">
      <div class="box"><label>${isClient ? 'Billed to' : 'Payable to'}</label><strong>${escapeHtml(partyName)}</strong><div class="muted">${escapeHtml(partyEmail)}</div></div>
      <div class="box"><label>Booking</label><strong>${escapeHtml(trackingId)}</strong><div class="muted">${escapeHtml(serviceName)}${scheduled ? ` · ${escapeHtml(scheduled)}` : ''}${time ? `<br />${escapeHtml(time)}` : ''}</div></div>
    </div>
    <table><thead><tr><th>Description</th><th class="right">Hours</th><th class="right">Rate</th><th class="right">Amount</th></tr></thead><tbody>${lineItems || '<tr><td colspan="4">No line items recorded.</td></tr>'}</tbody></table>
    <div class="totals">${clientTotals}<div class="total-row final"><span>${isClient ? 'Total' : 'Your earnings'}</span><span>${escapeHtml(money(invoice?.total, currency))}</span></div></div>
    <div class="footer">${isClient ? 'This client invoice can be viewed through the secure booking tracking link or the associated client account.' : 'This payout invoice is available only to the Gigger assigned to this booking.'} Payment and payout records are generated by GIGGS.</div>
  </main>
</body>
</html>`;
};

export const openInvoicePrint = (options) => {
  if (!options?.invoice) return false;
  const popup = window.open('', '_blank');
  if (!popup) return false;
  popup.opener = null;
  popup.document.open();
  popup.document.write(invoicePrintHtml(options));
  popup.document.close();
  return true;
};
